import React from 'react';
import {
  Grid,
  TextField,
  IconButton,
  Typography,
  Paper,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { FiTrash2 } from 'react-icons/fi';
import { LineItem } from '../../../../types/journalEntry';
import { FundingAllocationInputs } from '../../FundingAllocationInputs';
import { BasePaper, GlassInput } from './styles/SharedStyles';

interface LineItemRowProps {
  item: LineItem;
  index: number;
  error?: string;
  enableAllocations: boolean;
  onUpdate: (updated: Partial<LineItem>) => void;
  onDelete: () => void;
}

// Styled Components
const StyledPaper = styled(BasePaper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgb(34, 39, 58)',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#0EA5E9',
    },
    '&.Mui-error fieldset': {
      borderColor: '#ef4444',
    }
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#0EA5E9',
    },
    '&.Mui-error': {
      color: '#ef4444',
    }
  },
  '& .MuiInputBase-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiFormHelperText-root': {
    color: '#ef4444',
    marginLeft: '4px',
  }
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  color: '#ef4444',
  backgroundColor: 'rgba(239, 68, 68, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  padding: '8px',
}));

const AmountField = styled(StyledTextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: '#10B981',
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#10B981',
  }
}));

export const LineItemRow: React.FC<LineItemRowProps> = ({
  item,
  index,
  error,
  enableAllocations,
  onUpdate,
  onDelete
}) => {
  return (
    <StyledPaper 
      sx={{ 
        p: 2, 
        mb: 2, 
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)',
          opacity: error ? 0 : 1
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: '#ef4444',
          opacity: error ? 1 : 0
        }
      }}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <StyledTextField
            fullWidth
            label="Account"
            value={item.accountId}
            onChange={(e) => onUpdate({ accountId: e.target.value })}
            error={!!error}
            helperText={error}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StyledTextField
            fullWidth
            label="Description"
            value={item.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <AmountField
            fullWidth
            type="number"
            label="Debit"
            value={item.debit || ''}
            onChange={(e) => onUpdate({ debit: parseFloat(e.target.value) || 0 })}
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <AmountField
            fullWidth
            type="number"
            label="Credit"
            value={item.credit || ''}
            onChange={(e) => onUpdate({ credit: parseFloat(e.target.value) || 0 })}
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        </Grid>
        <Grid item xs={12} md={1}>
          <Tooltip title="Delete line item">
            <DeleteButton onClick={onDelete} size="small">
              <FiTrash2 />
            </DeleteButton>
          </Tooltip>
        </Grid>
      </Grid>

      {enableAllocations && (
        <Grid item xs={12} sx={{ mt: 2 }}>
          <FundingAllocationInputs
            allocations={item.allocations || []}
            onAllocationsChange={allocations => 
              onUpdate({ allocations })
            }
            variant="journal"
          />
        </Grid>
      )}
    </StyledPaper>
  );
}; 